import React, { useState } from 'react';
import { PhoneIcon, MailIcon, MapPinIcon, ClockIcon, SendIcon } from 'lucide-react';
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    matter: '',
    message: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission would be handled here
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  return <section id="contact" className="py-20 lg:py-28 bg-tlfc-cream" data-id="element-45">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10" data-id="element-46">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-47">
          {/* Left Column — Info */}
          <div className="reveal" data-id="element-48">
            <span className="text-xs uppercase tracking-wider text-tlfc-gold font-medium" data-id="element-49">
              Contact Us
            </span>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl text-tlfc-text leading-tight" data-id="element-50">
              Let's discuss your matter
            </h2>
            <p className="mt-4 text-tlfc-text-secondary text-lg leading-relaxed" data-id="element-51">
              Whether you have a question or are ready to get started, we're
              here to help. Reach out and one of our specialists will be in
              touch.
            </p>

            {/* Contact Details */}
            <div className="mt-10 space-y-6" data-id="element-52">
              <a href="tel:0386009333" className="flex items-start gap-4 group" data-id="element-53">
                <div className="w-12 h-12 rounded-xl bg-tlfc-gold/10 flex items-center justify-center flex-shrink-0" data-id="element-54">
                  <PhoneIcon className="w-5 h-5 text-tlfc-gold" aria-hidden="true" data-id="element-55" />
                </div>
                <div data-id="element-56">
                  <p className="font-medium text-tlfc-text group-hover:text-tlfc-gold transition-colors" data-id="element-57">
                    (03) 8600 9333
                  </p>
                  <p className="text-sm text-tlfc-text-secondary mt-1" data-id="element-58">
                    Call us directly
                  </p>
                </div>
              </a>

              <a href="mailto:info@tlfc.com.au" className="flex items-start gap-4 group" data-id="element-59">
                <div className="w-12 h-12 rounded-xl bg-tlfc-gold/10 flex items-center justify-center flex-shrink-0" data-id="element-60">
                  <MailIcon className="w-5 h-5 text-tlfc-gold" aria-hidden="true" data-id="element-61" />
                </div>
                <div data-id="element-62">
                  <p className="font-medium text-tlfc-text group-hover:text-tlfc-gold transition-colors" data-id="element-63">
                    info@tlfc.com.au
                  </p>
                  <p className="text-sm text-tlfc-text-secondary mt-1" data-id="element-64">
                    Email us anytime
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-4" data-id="element-65">
                <div className="w-12 h-12 rounded-xl bg-tlfc-gold/10 flex items-center justify-center flex-shrink-0" data-id="element-66">
                  <MapPinIcon className="w-5 h-5 text-tlfc-gold" aria-hidden="true" data-id="element-67" />
                </div>
                <div data-id="element-68">
                  <p className="font-medium text-tlfc-text" data-id="element-69">
                    Level 6, Bourke Place
                  </p>
                  <p className="text-sm text-tlfc-text-secondary mt-1" data-id="element-70">
                    600 Bourke Street, Melbourne VIC 3000
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-71">
                <div className="w-12 h-12 rounded-xl bg-tlfc-gold/10 flex items-center justify-center flex-shrink-0" data-id="element-72">
                  <ClockIcon className="w-5 h-5 text-tlfc-gold" aria-hidden="true" data-id="element-73" />
                </div>
                <div data-id="element-74">
                  <p className="font-medium text-tlfc-text" data-id="element-75">Monday – Friday</p>
                  <p className="text-sm text-tlfc-text-secondary mt-1" data-id="element-76">
                    9:00am – 5:30pm AEST
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column — Form */}
          <div className="reveal reveal-delay-2" data-id="element-77">
            <form onSubmit={handleSubmit} className="bg-white border border-tlfc-border rounded-2xl p-8 shadow-card" data-id="element-78">
              <h3 className="font-serif text-xl text-tlfc-text" data-id="element-79">
                Send us a message
              </h3>
              <p className="mt-2 text-sm text-tlfc-text-secondary" data-id="element-80">
                We'll get back to you within one business day.
              </p>

              <div className="mt-6 space-y-5" data-id="element-81">
                <div data-id="element-82">
                  <label htmlFor="name" className="block text-sm font-medium text-tlfc-text mb-2" data-id="element-83">
                    Full name *
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full bg-tlfc-cream border border-tlfc-border rounded-lg px-4 py-3 text-sm text-tlfc-text placeholder:text-tlfc-muted focus:border-tlfc-gold focus:outline-none transition-colors" placeholder="Your name" data-id="element-84" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-85">
                  <div data-id="element-86">
                    <label htmlFor="email" className="block text-sm font-medium text-tlfc-text mb-2" data-id="element-87">
                      Email *
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full bg-tlfc-cream border border-tlfc-border rounded-lg px-4 py-3 text-sm text-tlfc-text placeholder:text-tlfc-muted focus:border-tlfc-gold focus:outline-none transition-colors" placeholder="you@example.com" data-id="element-88" />
                  </div>
                  <div data-id="element-89">
                    <label htmlFor="phone" className="block text-sm font-medium text-tlfc-text mb-2" data-id="element-90">
                      Phone
                    </label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full bg-tlfc-cream border border-tlfc-border rounded-lg px-4 py-3 text-sm text-tlfc-text placeholder:text-tlfc-muted focus:border-tlfc-gold focus:outline-none transition-colors" placeholder="0400 000 000" data-id="element-91" />
                  </div>
                </div>

                <div data-id="element-92">
                  <label htmlFor="matter" className="block text-sm font-medium text-tlfc-text mb-2" data-id="element-93">
                    Type of matter
                  </label>
                  <select id="matter" name="matter" value={formData.matter} onChange={handleChange} className="w-full bg-tlfc-cream border border-tlfc-border rounded-lg px-4 py-3 text-sm text-tlfc-text focus:border-tlfc-gold focus:outline-none transition-colors" data-id="element-94">
                    <option value="" data-id="element-95">Select an area...</option>
                    <option value="business" data-id="element-96">Business & Corporate</option>
                    <option value="property" data-id="element-97">Property & Development</option>
                    <option value="litigation" data-id="element-98">Litigation & Disputes</option>
                    <option value="family" data-id="element-99">Family Law</option>
                    <option value="employment" data-id="element-100">Employment Law</option>
                    <option value="owners-corp" data-id="element-101">Owners Corporations</option>
                    <option value="other" data-id="element-102">Other / Not sure</option>
                  </select>
                </div>

                <div data-id="element-103">
                  <label htmlFor="message" className="block text-sm font-medium text-tlfc-text mb-2" data-id="element-104">
                    How can we help? *
                  </label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={4} className="w-full bg-tlfc-cream border border-tlfc-border rounded-lg px-4 py-3 text-sm text-tlfc-text placeholder:text-tlfc-muted focus:border-tlfc-gold focus:outline-none transition-colors resize-none" placeholder="Briefly describe your situation..." data-id="element-105" />
                </div>

                <button type="submit" className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-tlfc-gold text-white font-medium rounded-lg hover:bg-tlfc-gold-light transition-colors shadow-sm" data-id="element-106">
                  Send message
                  <SendIcon className="w-4 h-4" aria-hidden="true" data-id="element-107" />
                </button>

                <p className="text-xs text-tlfc-muted text-center" data-id="element-108">
                  By submitting, you agree to our Privacy Policy. We'll never
                  share your information.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>;
}